import { Entity, Column, PrimaryGeneratedColumn, ManyToOne, Index, JoinColumn, OneToMany, OneToOne } from 'typeorm'

import Question from './survey/Question'
import RespondentSurvey from './RespondentSurvey'
import ResponseEvent from './ResponseEvent'
import { QuestionType } from './entityTypes'
import { JSONObject } from '../JsonType'
import ResponseMetaData from './ResponseMetaData'

export function createRrdbResponseJoinId (rrdbSessionId: string, rrdbQuestionId: string) {
  return `${rrdbSessionId}_${rrdbQuestionId}`
}

@Entity('responses')
export default class Response {
  @PrimaryGeneratedColumn('uuid')
  id!: string

  @Column({ name: 'client_id' })
  clientId?: string

  @Column({ name: 'created_at' })
  createdAt!: Date

  // The ID of the RRDB document that generated this Response.
  @Column({ name: 'rrdb_document_id' })
  @Index({ unique: true })
  rrdbDocumentId!: string

  @Column({
    name: 'question_type',
    type: 'enum',
    enum: QuestionType
  })
  questionType!: QuestionType

  @ManyToOne(type => Question, question => question.responses)
  @JoinColumn({ name: 'question_id' })
  question!: Question

  @Column({ name: 'question_id' })
  questionId!: string

  // The ID of the RRDB document that generated the explainAnswer for this Response.
  @Column({ name: 'rrdb_explain_document_id', nullable: true })
  @Index({ unique: true })
  rrdbExplainDocumentId?: string

  @Column({ name: 'other_answer', nullable: true })
  otherAnswer?: string

  @Column({ name: 'explain_answer', nullable: true })
  explainAnswer?: string

  @Column({ name: 'text_answer' })
  textAnswer?: string

  @Column({ name: 'text_array_answer', type: 'text', array: true })
  textArrayAnswer?: string[]

  @Column({ name: 'text_map_answer', type: 'jsonb', array: false })
  textMapAnswer?: JSONObject

  @ManyToOne(type => RespondentSurvey, s => s.responses)
  @JoinColumn({ name: 'respondent_survey_id' })
  respondentSurvey!: RespondentSurvey

  @Column({ name: 'respondent_survey_id' })
  respondentSurveyId?: string

  @OneToMany(type => ResponseEvent, r => r.response)
  responseEvents?: ResponseEvent[]

  // This property allows channel to link other/explain answers to their original
  // responses.
  @Column({ name: 'rrdb_response_join_id' })
  @Index({ unique: true })
  rrdbResponseJoinId!: string

  @OneToOne(type => ResponseMetaData, responseMetaData => responseMetaData.response)
  metaData?: ResponseMetaData

  // This property allows channel to link this Response with the associated channel DB
  // Question during upsert. This property itself is not persisted to channel DB.
  rrdbQuestionId!: string

  // This property allows channel to determine RespondentSession state based on
  // the Responses under a RespondentSession. This property itself is not persisted to
  // channel DB.
  rrdbSessionId!: string

  // This property allows channel to link this Response with the associated RespondentSurvey
  // during upsert. This property itself is not persisted to channel DB.
  rrdbRespondentSurveyJoinId!: string

  // This property allows channel to log the offset of the message that created this record.
  // This property itself is not persisted to channel DB.
  kafkaResponseTopicOffset!: number

  // This property allows channel to correctly determine RespondentSurvey state based
  // on the Responses under a RespondentSurvey. This property itself is not persisted to
  // channel DB.
  completesSurvey!: boolean

  // This property indicates whether the response failed a red-herring question.
  // This property itself is not persisted to channel DB, and is only used for
  // computing input for the State API.
  failedRedHerring!: boolean
}
